import { Capacitor } from "@capacitor/core";
import { apiClient, kernelClient, uploadClient } from "./api";
import { AUTH_ENDPOINTS } from "./endpoints";
import { Preferences } from "@capacitor/preferences";
import { Device } from "@capacitor/device";
import {
  LocalNotifications,
  LocalNotificationSchema,
} from "@capacitor/local-notifications";
import { isPlatform } from "@ionic/react";

/* eslint-disable @typescript-eslint/no-explicit-any */

// Address interface
interface Address {
  street: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
}
// Diagnosis interface
export interface Diagnosis {
  created_at: string;
  updated_at: string;
  id: number;
  code: string;
  description: string;
  category: string | null;
  is_active: boolean;
}
export interface SocialAccount {
  type: string;
  connected: boolean;
  isDisconnected: boolean;
  disconnectedAt: string | null;
  canLogin: boolean;
}

export interface UserStatusResponse {
  status: string;
  exists: boolean;
  type: string;
  isNewUser: boolean;
  hasAgreedTerms: boolean;
  user: {
    profile_id: number;
    role: string;
    status: string;
  };
  canLogin: boolean;
  message?: string;
}

// Settings interface
interface Settings {
  notifications: boolean;
  darkMode: boolean;
  language: string;
  [key: string]: any;
}

// UserPatient interface
export interface UserPatient {
  id?: number;
  name: string;
  avatar: string | null;
  profile_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  hash: string;
  role: string;
  details: any | null;
  address: Address;
  created_at: string;
  updated_at: string;
  phone_country_code: string;
  photo_uuid: string;
  insurance: any | null;
  condition: {
    height?: number;
    weight?: number;
    gender?: string;
    blood_type?: string;
    existing_conditions?: string[];
    allergies?: string[];
  } | null;
  clinician: any | null;
  date_of_birth: string | null;
  quickblox_password: null;
  quickblox_id: null;
  utc_offset: number | null;
  time_zone: string | null;
  status: string;
  user_name: string | null;
  note: string | null;
  patient_id: any | null;
  settings: Settings;
  login_source: string | null;
  google_account: any | null;
  facebook_account: any | null;
  apple_account: any | null;
  linked_accounts: Record<string, any>;
  hasSetBiometrics: boolean;
  hasCompletedOnboarding: boolean;
  gender: string | null;
  existing_conditions: string[] | null;
  allergies: string[] | null;
}

/**
 * Gets the current user's profile from the API
 * @returns User profile data
 */
export const getCurrentUser = async () => {
  try {
    // Try to get cached user data from Preferences first
    const cachedData = await Preferences.get({ key: "user" });

    if (cachedData.value) {
      return JSON.parse(cachedData.value);
    }

    // If no cached data, fetch from API
    // const response = await apiClient.get("/user/profile", {});

    // // Cache the response for future use
    // await Preferences.set({
    //   key: "user",
    //   value: JSON.stringify(response.data),
    // });

    // return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

/**
 * Creates a new user profile
 * @param userData User data to create account with
 * @returns Created user data
 */
export const createUser = async (
  userData: Partial<UserPatient>,
): Promise<UserPatient> => {
  try {
    const uniqueIdentifier = (await Device.getId()).identifier;
    const response = await apiClient.post("/user/create", {
      ...userData,
      handset_id: uniqueIdentifier,
      platform: Capacitor.getPlatform(),
    });

    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

/**
 * Updates the current user's profile
 * @param userData User data to update
 * @returns Updated user data
 */
export const updateProfile = async (
  userData: Partial<UserPatient>,
): Promise<UserPatient> => {
  try {
    // Extract profile_id from current user if not provided
    if (!userData.profile_id) {
      const currentUser = await getCurrentUser();
      userData.profile_id = currentUser.profile_id;
    }
    const response = await apiClient.put("/user/updateProfile", userData, {});
    return response.data;
  } catch (error: any) {
    console.error("Error updating user profile:", error);
    throw error.response?.data || error;
  }
};

/**
 * Updates user settings
 * @param settings User settings to update
 * @returns Updated settings
 */
export const updateUserSettings = async (
  settings: Partial<Settings>,
): Promise<Settings> => {
  try {
    const response = await apiClient.put("/user/settings", settings, {});

    return response.data;
  } catch (error) {
    console.error("Error updating user settings:", error);
    throw error;
  }
};

/**
 * Updates the user's profile picture
 * @param imageData Base64 encoded image data or file object
 * @returns Updated user data with new avatar URL
 */
export const uploadProfilePicture = async (file: File): Promise<any> => {
  try {
    const formData = new FormData();
    if (!file) throw new Error("No file provided for upload");
    formData.append("profilePicture", file, file.name);

    const headers: Record<string, string> = {};

    // Was a raw fetch with a hand-built Authorization header and therefore NO refresh
    // path. Harmless on a 1-day blaze token; broken constantly on a 15-minute Cognito
    // access token. uploadClient already carries the shared-refresh interceptor and the
    // multipart content type — reuse it rather than adding a second authed-fetch path.
    const uploadResponse = await uploadClient.put(
      `/user/updateProfilePicture`,
      formData,
      { headers },
    );

    const responseData = uploadResponse.data;

    if (
      responseData.data.profilePictureUrl &&
      responseData.status === "success"
    ) {
      return responseData.data.profilePictureUrl;
    }

    throw new Error(responseData.message || "Failed to upload profile picture");
  } catch (error: any) {
    console.error("Error uploading profile picture:", error);
    throw new Error(
      error.response?.data?.message || "Failed to upload profile picture",
    );
  }
};

/**
 * Deletes the current user's account
 * @param confirmationPassword User's password for confirmation
 * @returns Success status
 */
export const deleteUserAccount = async (
  confirmationPassword?: string,
): Promise<{ success: boolean; response: any }> => {
  try {
    const response = await apiClient.delete("/user/account", {
      data: { confirmationPassword },
    });

    localStorage.removeItem("token");
    await Preferences.remove({ key: "token" });

    return { success: true, response: response.data };
  } catch (error) {
    console.error("Error deleting user account:", error);
    throw error;
  }
};

/**
 * Changes the user's password
 * @param currentPassword Current password for verification
 * @param newPassword New password to set
 * @returns Success status
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<{ success: boolean; response: any }> => {
  try {
    const response = await apiClient.put(
      "/user/password",
      {
        currentPassword,
        newPassword,
      },
      {},
    );

    return { success: true, response: response.data };
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

/**
 * Initiates password reset process
 * @param email User's email address
 * @returns Success status
 */
export const requestPasswordReset = async (
  email: string,
): Promise<{ success: boolean; response: any }> => {
  try {
    const response = await apiClient.post("/user/password/reset-request", {
      email,
    });

    return { success: true, response: response.data };
  } catch (error) {
    console.error("Error requesting password reset:", error);
    throw error;
  }
};

/**
 * Gets user activity history
 * @param page Page number for pagination
 * @param limit Number of items per page
 * @returns User activity data
 */
export const getUserActivity = async (page = 1, limit = 10): Promise<any> => {
  try {
    const response = await apiClient.get("/user/activity", {
      params: { page, limit },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching user activity:", error);
    throw error;
  }
};
export const getAllDiagnoses = async (): Promise<any> => {
  try {
    const response = await apiClient.get("/api/diagnosis");
    return response.data;
  } catch (error) {
    console.error("Error fetching diagnoses:", error);
    throw error;
  }
};

export const getUserPreferences = async (preferences?: any): Promise<any> => {
  try {
    const response = await apiClient.get("/api/user-preferences", preferences);
    return response.data || {};
  } catch (error) {
    console.error("Error fetching user preferences:", error);
    throw error;
  }
};

export const updateUserPreferences = async (
  preferences: any,
): Promise<{ success: boolean }> => {
  try {
    await apiClient.put("/api/user-preferences", preferences);
    return { success: true };
  } catch (error) {
    console.error("Error updating user preferences:", error);
    throw error;
  }
};

export const saveNotificationsSettings = async (
  data: any,
): Promise<{ success: boolean; response: any }> => {
  try {
    const response = await apiClient.put("/api/notification-settings", data);
    return { success: true, response: response.data };
  } catch (error) {
    console.error("Error saving notifications settings:", error);
    throw error;
  }
};

export const getPendingNotifications = async (
  profile_id: number,
  fcm_token: string | null,
  notifications: any[],
): Promise<any> => {
  try {
    const pendingNotifications = await apiClient.post(
      "/api/notification-settings-pending",
      {
        profile_id,
        fcm_token,
        notifications,
      },
    );
    return pendingNotifications.data;
  } catch (error) {
    console.error(
      "Error updating pending notifications:",
      JSON.stringify(error),
    );
    throw error;
  }
};

export const retrieveNotificationsSettings = async (
  userId?: number,
): Promise<any> => {
  try {
    const url = userId
      ? `/api/notification-settings?set_for=${userId}`
      : `/api/notification-settings`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error("Error retrieving notifications settings:", error);
    throw error;
  }
};
export const createNotificationSettings = async (
  data: any,
): Promise<{ success: boolean }> => {
  try {
    await apiClient.post("/api/notification-settings", data);
    return { success: true };
  } catch (error) {
    console.error("Error creating notification settings:", error);
    throw error;
  }
};
export const deleteNotificationSettings = async (): Promise<{
  success: boolean;
}> => {
  try {
    await apiClient.delete("/api/notification-settings");
    return { success: true };
  } catch (error) {
    console.error("Error deleting notification settings:", error);
    throw error;
  }
};
export const changeUserPhone = async (
  new_phone: string,
  phone_country_code?: string,
  profile_id?: number,
): Promise<{ success: boolean }> => {
  try {
    if (!profile_id) {
      const currentUser = await getCurrentUser();
      profile_id = currentUser.profile_id;
    }
    const response = await apiClient.post("/api/change-phone-number", {
      new_phone,
      profile_id,
      phone_country_code,
    });

    return response.data;
  } catch (error) {
    console.error("Error changing phone number:", error);
    throw error;
  }
};
export const verifyPhoneNumber = async (
  token: string,
  code: string,
): Promise<any> => {
  try {
    let profile_id: number | undefined;
    if (!profile_id) {
      const currentUser = await getCurrentUser();
      profile_id = currentUser.profile_id;
    }
    const response = await apiClient.put("/api/Verify-change-PhoneOtp", {
      token,
      code,
      profile_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error verifying code:", error);
    throw error;
  }
};
export const changeUserEmail = async (
  new_email: string,
  profile_id?: number,
): Promise<{ success: boolean }> => {
  try {
    if (!profile_id) {
      const currentUser = await getCurrentUser();
      profile_id = currentUser.profile_id;
    }
    const response = await apiClient.post("/api/change-email", {
      new_email,
      profile_id,
    });

    return response.data;
  } catch (error) {
    console.error("Error changing email:", error);
    throw error;
  }
};
export const verifyEmail = async (
  token: string,
  code: string,
): Promise<any> => {
  try {
    let profile_id: number | undefined;
    if (!profile_id) {
      const currentUser = await getCurrentUser();
      profile_id = currentUser.profile_id;
    }
    const response = await apiClient.put("/api/Verify-change-EmailOtp", {
      token,
      code,
      profile_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error verifying email code:", error);
    throw error;
  }
};

export const getProfileById = async (profile_id?: number): Promise<any> => {
  try {
    // ⚠️ Do NOT reintroduce a getCurrentUser() lookup here. The endpoint takes no id --
    // blaze derives the profile from the token -- so the old `if (!profile_id)` branch
    // fetched an id it then never sent. Harmless in steady state and fatal on a FRESH
    // LOGIN, which is the one moment it matters: there is no stored current user yet
    // (that is what we are fetching), so it threw, the post-login batch swallowed it as a
    // tolerant leg, setUser never ran, and the app bounced back to Sign In having just
    // authenticated successfully.
    //
    // `profile_id` is kept in the signature only because callers pass it; it is unused.
    const response = await apiClient.get(`/api/getProfileById`);
    return response.data;
  } catch (error) {
    console.error("Error fetching profile by ID:", error);
    throw error;
  }
};
export const checkUserExists = async (
  email: string | undefined,
  phone: string | undefined,
  identification_type?: string,
  social_id?: string,
): Promise<UserStatusResponse> => {
  try {
    console.log("Checking user existence with:", {
      email,
      phone,
      identification_type,
      social_id,
    });
    if (!email && !phone) {
      throw new Error(
        "Either email or phone must be provided to check user existence.",
      );
    }
    const response = await apiClient.post("/api/login/user-exists", {
      email,
      phone,
      identification_type,
      social_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error checking if user exists:", error);
    throw error;
  }
};
export type AccountDeletedResponse = {
  profile_id: number;
  status: string;
  anonymized_email: string;
};

/**
 * Soft-delete the caller's own account (kernel `DELETE /me`).
 */
export const softDeleteAccount = async (): Promise<AccountDeletedResponse> => {
  try {
    const response = await kernelClient.delete<AccountDeletedResponse>(
      AUTH_ENDPOINTS.KERNEL_SOFT_DELETE,
    );
    return response.data;
  } catch (error) {
    console.error("Error soft deleting user account:", error);
    throw error;
  }
};

export const reAssignNotifications = async (allNotify: any) => {
  const pending: any = await LocalNotifications.getPending();
  const dailyReminder = allNotify.find((n: any) => n.type === "DAILY_REMINDER");
  if (allNotify.length > 0 && dailyReminder && dailyReminder.message) {
    const dailyReminderTime = dailyReminder.schedule.time;

    if (
      !dailyReminderTime ||
      typeof dailyReminderTime !== "string" ||
      !dailyReminderTime.includes(":")
    ) {
      throw new Error(
        "daily_reminder_time is invalid or missing in notification settings.",
      );
    }

    const [hoursStr, minutesStr] = dailyReminderTime.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error("Invalid hour or minute in daily_reminder_time.");
    }

    // Check for existing notification with matching time and type
    const existingNotification = pending.notifications?.find(
      (n: any) =>
        n.extra?.type === "daily-reminder" &&
        n.schedule?.on?.hour === hours &&
        n.schedule?.on?.minute === minutes,
    );

    if (existingNotification) {
      return;
    }

    // Cancel all existing notifications of type "daily-reminder"
    else if (pending.notifications && pending.notifications.length > 0) {
      const toCancel = pending.notifications
        .filter((n: any) => n.extra?.type === "daily-reminder")
        .map((n: any) => ({ id: n.id }));

      if (toCancel.length > 0) {
        await LocalNotifications.cancel({ notifications: toCancel });
      }
    }

    // Create and schedule new notification
    const scheduleDate = new Date();
    scheduleDate.setHours(hours, minutes, 0, 0);

    if (scheduleDate.getTime() <= new Date().getTime()) {
      scheduleDate.setDate(scheduleDate.getDate() + 1);
    }

    let uniqueId: number;
    if (isPlatform("android")) {
      uniqueId = Math.floor(Math.random() * 2147483647);
    } else {
      uniqueId = Date.now();
    }

    const notifications: LocalNotificationSchema[] = [
      {
        schedule: {
          on: {
            hour: scheduleDate.getHours(),
            minute: scheduleDate.getMinutes(),
          },
          allowWhileIdle: true,
          repeats: true,
        },
        title: "Daily Reminder",
        body: "It's time for BiWaze® therapy!",
        id: uniqueId,
        sound: "default",
        attachments: undefined,
        actionTypeId: "",
        extra: {
          type: "daily-reminder",
          scheduledTime: dailyReminderTime,
        },
      },
    ];

    // Check and request permissions
    const permissionResult = await LocalNotifications.checkPermissions();
    if (!permissionResult || permissionResult.display !== "granted") {
      await LocalNotifications.requestPermissions();
    }

    if (isPlatform("android")) {
      const isExactNotificationEnabled =
        await LocalNotifications.checkExactNotificationSetting();
      if (!isExactNotificationEnabled) {
        await LocalNotifications.changeExactNotificationSetting();
      }
    }
    const isGrantedPermissions = await LocalNotifications.checkPermissions();
    if (isGrantedPermissions && isGrantedPermissions.display === "granted") {
      await LocalNotifications.schedule({ notifications });
    }
  } else {
    // Remove all notifications if daily reminder is disabled
    if (pending.notifications && pending.notifications.length > 0) {
      await LocalNotifications.cancel({
        notifications: pending.notifications.map((n: any) => ({ id: n.id })),
      });
    }
  }
};
